/**
 * The "boss dusk" hero moment (ART_WOW_PASS Phase 2.3, lighting review §4.1): while a boss lives, the map sinks
 * toward dusk — a low orange sun, a dimmer and cooler fill, a warmer hazier horizon, heavier cloud shade and lanterns
 * whose glow breathes twice as deep — and when the last boss falls, "the sun breaks through": the dusk lifts and the
 * light overshoots warm and bright for a couple of seconds before settling back to the afternoon.
 *
 * This file is the timeline only: pure numbers, no three.js, unit-tested. `SceneRig` owns an instance, turns its two
 * weights into a look blend every frame (`SceneRig.blendLooks(BOSS_DUSK, dusk, SUN_BREAK, sun)` — lights, exposure,
 * sky dome, fog, haze and grade; never the PMREM), and resets it on every new map.
 */
export class BossDusk {
  /** Seconds to sink into dusk after a boss appears (smoothstep). */
  static readonly InSeconds = 2;
  /** Seconds for the dusk to lift after the last boss dies. */
  static readonly OutSeconds = 1.6;
  /** The sun-break overshoot: rises over `BreakRise`, holds, and has fully settled by `BreakSeconds`. */
  static readonly BreakRise = 0.6;
  static readonly BreakSeconds = 3.2;

  private bosses = 0;
  /** Linear dusk progress 0..1; the weight is its smoothstep. */
  private p = 0;
  /** Seconds since the sun-break started; < 0 when there is none. */
  private tb = -1;
  /** The weights for this frame (read after `update`). Reused: no allocation per frame. */
  readonly weights = { dusk: 0, sun: 0, active: false };

  /** A boss appeared (any number may overlap; dusk holds until the last one dies). */
  bossSpawned(): void { this.bosses++; }

  /** A boss died. The last one lifts the dusk and starts the sun-break. */
  bossDied(): void {
    if (this.bosses <= 0) return;
    this.bosses--;
    if (this.bosses === 0) this.tb = 0;
  }

  /** Straight back to afternoon, no transition (a new map, a restart, leaving the level). */
  reset(): void { this.bosses = 0; this.p = 0; this.tb = -1; this.weights.dusk = 0; this.weights.sun = 0; this.weights.active = false; }

  get bossesAlive(): number { return this.bosses; }

  update(dt: number): typeof this.weights {
    const step = this.bosses > 0 ? dt / BossDusk.InSeconds : -dt / BossDusk.OutSeconds;
    this.p = Math.max(0, Math.min(1, this.p + step));
    let sun = 0;
    if (this.tb >= 0) {
      this.tb += dt;
      const t = this.tb;
      if (t >= BossDusk.BreakSeconds || this.bosses > 0) this.tb = -1;          // done, or a new boss cut it short
      else if (t < BossDusk.BreakRise) { const u = t / BossDusk.BreakRise; sun = 1 - (1 - u) * (1 - u); }   // ease-out up
      else { const u = (t - BossDusk.BreakRise) / (BossDusk.BreakSeconds - BossDusk.BreakRise); sun = 1 - u * u * (3 - 2 * u); }
    }
    const w = this.weights;
    w.dusk = this.p * this.p * (3 - 2 * this.p);
    w.sun = sun;
    w.active = w.dusk > 0 || w.sun > 0;
    return w;
  }
}

/**
 * Subscribes a BossDusk to the enemies view's boss events. Tolerant of both shapes the hook has had: the Phase 2
 * listener ARRAYS (`onBossSpawn.push(fn)`, units' `p2-units`) and the Phase 1 single nullable callback — so this is one
 * line in the level controller whichever lands first. Returns an unsubscribe.
 */
export function subscribeBossDusk(enemies: object, dusk: BossDusk, skip: (boss: unknown) => boolean = () => false): () => void {
  const hooks: (() => void)[] = [];
  const attach = (name: "onBossSpawn" | "onBossDeath" | "onBossGone", fn: () => void): void => {
    const e = enemies as Record<string, unknown>;
    const slot = e[name];
    if (Array.isArray(slot)) {
      slot.push(fn);
      hooks.push(() => { const i = slot.indexOf(fn); if (i >= 0) slot.splice(i, 1); });
    } else if (slot === null || slot === undefined || typeof slot === "function") {
      const prev = slot as ((...a: unknown[]) => void) | null | undefined;
      const both = (...a: unknown[]): void => { prev?.(...a); fn(); };
      e[name] = both;
      hooks.push(() => { if (e[name] === both) e[name] = prev ?? null; });
    }
  };
  // M36: a rare (`skip`) brings its own golden night (rareLook.ts), not the dusk
  attach("onBossSpawn", ((b: unknown) => { if (!skip(b)) dusk.bossSpawned(); }) as () => void);
  attach("onBossDeath", ((b: unknown) => { if (!skip(b)) dusk.bossDied(); }) as () => void);
  if (Array.isArray((enemies as Record<string, unknown>).onBossGone)) attach("onBossGone", ((b: unknown) => { if (!skip(b)) dusk.bossDied(); }) as () => void);   // walked in: no fall, but the dusk lifts
  return () => { for (const h of hooks) h(); };
}
